<template>
  <div>
    <ModalDYDialog title="전자결재 서명" type="small" :dialog-control="modalToggle"
      @update:dialog-control="emit('update:modalToggle', $event)">
      <form class="form" ref="signForm" @submit.prevent="submit">
        <div class="flex flex-wrap w-full !text-sm">
          <div class="flex w-full">
            <div class="col-2 px-0 flex items-center">타입 : </div>
            <v-radio-group v-model:model-value="formData.work_type" inline hide-details="auto">
              <v-radio label="출근" value="start" :disabled="startCommute ? true : false"></v-radio>
              <v-radio :label="startCommute ? `퇴근예정은 ${abailableEndTime?.format('HH:mm')} 입니다.` : '퇴근'" value="end" :disabled="endCommute ? true : false"></v-radio>
            </v-radio-group>
          </div>

          <v-alert v-if="startCommute" class="my-3 !col-12 !text-sm" color="blue" variant="tonal" density="compact">이미 출근 체크를 하셨습니다</v-alert>

          <div class="flex w-full items-center">
            <div class="col-2 px-0 flex items-center flex-wrap">서명 : </div>
            <v-text-field v-model="formData.sign" class="!rounded-none" placeholder="서명자 암호" variant="outlined"
              type="password" density="compact" hide-details="auto"></v-text-field>
            <v-btn @click="check" rounded="0" class="!rounded-none !h-[39px]" color="green">서명확인</v-btn>
          </div>
        </div>

        <div class="py-3 mt-3 sm:flex sm:flex-row-reverse">
          <button type="submit"
            class="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-xs font-semibold text-white shadow-sm hover:bg-blue-500 sm:ml-3 sm:w-auto">확인</button>
          <button type="button"
            class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
            @click="emit('update:modalToggle', false)" ref="cancelButtonRef">취소</button>
        </div>
      </form>
    </ModalDYDialog>
  </div>
</template>

<script setup lang='ts'>
import { notify } from '@kyvg/vue3-notification';
import dayjs from 'dayjs';
import { cloneDeep } from 'lodash-es';
import { useDYComponent } from '~/modules/nuxt3-module-doyac/composables/dy-component';
import { $dy__fetchWithToken } from '~/modules/nuxt3-module-doyac/composables/fetch';
import { useElectronicAuthorizationApi } from '../../composables/dya-electronic-authorization';
import type { CommuteRecord } from '../DYADefaultTopMenu.vue';
import { useDYAppConfig } from '~/modules/nuxt3-module-doyac/composables/config';

const dyComponent = useDYComponent()

const props = withDefaults(defineProps<{
  modalToggle: boolean
  records?: CommuteRecord[]
}>(), {
  modalToggle: () => false,
})

const { checkSign } = useElectronicAuthorizationApi()
const { API_BASE_URL } = useDYAppConfig()

const emit = defineEmits<{
  (e: 'update:modalToggle', value: boolean): void,
  (e: 'request-fetch'): void,
}>()

// ------------------ 인터페이스 ------------------

// ------------------ 데이터 ------------------

const isSubmit = ref(false)
const formData = ref<{work_type:string, sign:string}>({
  work_type: 'start',
  sign: ''
})
const isSigned = ref(false)

// ------------------ 컴퓨티드 ------------------

const startCommute = computed(() => {
  if (!props.records || props.records.length < 1) return undefined
  return dayjs(props.records[0].work_time)
})

const endCommute = computed(() => { //레코드가 2개가 잡히면 출퇴근을 다 했다는 것임
  if (!props.records || props.records.length < 1 || !isCompleteCommuteCheck.value) return undefined
  return dayjs(props.records[1].work_time)
})

const abailableEndTime = computed(()=>{
  if (!props.records || props.records.length < 1 || !startCommute.value) return undefined

  const start = cloneDeep(startCommute.value)
  return start.add(9,'h')
})

const isCompleteCommuteCheck = computed(() => { //2개가 잡혀야 출퇴근 다 했다는 얘기임
  return props.records && props.records.length === 2
})

// ------------------ 메서드 ------------------

const check = async () => {
  const result = await checkSign(formData.value.sign)
  notify({ type: result.result, text: result.message })

  if (result.result === 'success') isSigned.value = true
}

const submit = async () => {
  if (!formData.value.work_type) {
    notify({ type: 'error', text: '출근 또는 퇴근을 선택해주세요' })
    return
  }

  if (!isSigned.value) {
    notify({ type: 'error', text: '먼저 서명해야 합니다' })
    return
  }
  
  isSubmit.value = true

  try {
    const result = await $dy__fetchWithToken<{ message: string, work_time:string, signature_time:string }>(`${API_BASE_URL}/api/commute-records`, { method: 'post', body: { work_type:formData.value.work_type } })
    const workTime = dayjs(result.work_time)

    notify({ type: 'success', text: formData.value.work_type === 'start' ? `출근처리되었습니다. ${workTime.format('HH:mm')}분부터 근무시작입니다` : '퇴근처리되었습니다. 수고하셨습니다' })
    emit('request-fetch')
  } catch (e: any) {
    console.error(e)
    $dy__notifyError(e)
    isSubmit.value = false
  }

  emit('update:modalToggle', false)
}

// ------------------ 훅 ------------------

onMounted(async () => {
  await nextTick()

})

dyComponent.$onActivated(async () => {
  await nextTick()

  // 다시 모달 열 때마다 사인 초기화
  isSigned.value = false
})

watch(startCommute, (val) => { //이미 출근체크 한 번 했으면 기본적으로 퇴근체크로 세팅
  if(val) formData.value.work_type = 'end'
})

</script>

<style scoped lang='scss'>
//</style>